// File: src/components/layouts/Legal/LegalPageLayout.tsx

import React, { Fragment, useState } from "react";
import { Typography } from "../../general";
import { Container, Flex, Divider } from "../../layouts/wrappers";
import { ChevronDown, ChevronUp } from "lucide-react";
import styled from "styled-components";
import Markdown from "markdown-to-jsx";

export type LegalSection = {
  heading: string;
  content: React.ReactNode;
};

interface LegalPageLayoutProps {
  title: string;
  lastUpdated?: string;
  sections?: LegalSection[];
  children?: React.ReactNode;
}
interface AccordionHeaderProps extends React.ComponentProps<typeof Flex> {
  onClick?: () => void;
}
const AccordionHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xs} 0;
  }
`;

const AccordionContent = styled.div<{ expanded: boolean }>`
  max-height: ${({ expanded }) => (expanded ? "1000px" : "0px")};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${({ expanded, theme }) => (expanded ? `${theme.spacing.sm} 0` : "0")};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ expanded, theme }) => (expanded ? `${theme.spacing.xs} 0` : "0")};
  }
`;

const ResponsiveContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  lastUpdated,
  sections,
  children,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <ResponsiveContainer maxWidth="full">
      <Flex direction="column" gap="sm" align="center" fullWidth>
        {/* Header */}
        <Flex
          direction="column"
          align="center"
          gap="xs"
          // maxWidth="64ch"
          style={{ textAlign: "center" }}
        >
          <Typography
            variant="display"
            align="center"
            style={{ fontSize: "2rem" }}
          >
            {title}
          </Typography>
          {lastUpdated && (
            <Typography
              as="span"
              variant="caption"
              align="center"
              colorScheme="textSecondary"
            >
              Last updated: {lastUpdated}
            </Typography>
          )}
        </Flex>

        <Divider />

        <Flex direction="column" gap="sm" fullWidth maxWidth="104ch">
          {sections ? (
            sections.map((section, idx) => {
              const isOpen = openIndex === idx;
              return (
                <Fragment key={idx}>
                  <AccordionHeader onClick={() => setOpenIndex(isOpen ? null : idx)}>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                      }}
                    >
                      {section.heading}
                    </Typography>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </AccordionHeader>

                  <AccordionContent expanded={isOpen}>
                  <Markdown
                    options={{
                      forceBlock: true,
                      overrides: {
                        h1: {
                          component: Typography,
                          props: { variant: "heading2", as: "h1" },
                        },
                        h2: {
                          component: Typography,
                          props: { variant: "heading3", as: "h2", style: { marginBottom: "0.5rem" } },
                        },
                        p: {
                          component: Typography,
                          props: {
                            variant: "bodySmall",
                            colorScheme: "textSecondary",
                            as: "p",
                            style: { marginBottom: "1rem", lineHeight: "1.6" },
                          },
                        },
                        ul: {
                          component: "ul",
                          props: {
                            style: {
                              paddingLeft: "1.5rem",
                              marginBottom: "1.25rem",
                              listStyleType: "disc",  // ✅ This enables bullet points
                            },
                          },
                        },
                        li: {
                          component: Typography,
                          props: {
                            as: "li",
                            variant: "bodySmall",
                            style: {
                              marginBottom: "0.5rem",
                              listStyleType: "inherit", // ✅ Inherits the disc style from <ul>
                            },
                          },
                        },
                        a: {
                          component: "a",
                          props: {
                            style: {
                              color: "#7CA22A",
                              textDecoration: "underline",
                            },
                          },
                        },
                      },
                    }}
                  >
                    {section.content as string}
                  </Markdown>
                  </AccordionContent>
                </Fragment>
              );
            })
          ) : (
            <Typography
              as="div"
              variant="bodySmall"
              colorScheme="textSecondary"
              style={{ fontSize: "0.9rem" }}
            >
              {children}
            </Typography>
          )}
        </Flex>
      </Flex>
    </ResponsiveContainer>
  );
};